import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/common/ConfirmProvider';
import { Panel } from '../../components/common/Panel';
import { CardSkeleton } from '../../components/common/Skeleton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

export const Route = createFileRoute('/household/')({
  component: HouseholdPage,
});

function HouseholdPage() {
  usePageTitle('Hushåll');
  const { confirm } = useConfirm();
  const householdQuery = trpc.household.get.useQuery(undefined, {
    retry: false,
  });
  const utils = trpc.useUtils();

  const inviteMutation = trpc.household.invite.useMutation({
    onError: (err) => toast.error(err.message),
  });
  const addMemberMutation = trpc.household.addMember.useMutation({
    onSuccess: () => {
      void utils.household.get.invalidate();
      toast.success('Medlem tillagd');
    },
    onError: (err) => toast.error(err.message),
  });
  const removeMutation = trpc.household.removeMember.useMutation({
    onSuccess: () => {
      void utils.household.get.invalidate();
      toast.success('Medlem borttagen');
    },
    onError: (err) => toast.error(err.message),
  });

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [newMemberName, setNewMemberName] = useState('');

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate(
      { email: inviteEmail.trim() },
      {
        onSuccess: (data) => {
          setInviteLink(data.inviteUrl);
          setInviteEmail('');
          toast.success('Inbjudan skickad');
        },
      },
    );
  };

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemberName.trim()) return;
    addMemberMutation.mutate(
      { displayName: newMemberName.trim() },
      { onSuccess: () => setNewMemberName('') },
    );
  };

  if (householdQuery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <CardSkeleton lines={4} />
        <CardSkeleton lines={2} />
        <CardSkeleton lines={2} />
      </div>
    );
  }

  if (householdQuery.error) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl bg-negative-soft p-4 text-sm text-negative">
        {householdQuery.error.message}
      </div>
    );
  }

  const household = householdQuery.data;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{household?.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Hushållsinställningar</p>
      </header>

      <Panel title="Medlemmar" className="pb-1 sm:pb-2">
        <ul className="-mx-4 divide-y divide-border sm:-mx-5">
          {household?.members?.map(
            (m: {
              id: string;
              displayName: string;
              email?: string;
              role: string;
              isLinkedUser?: boolean;
            }) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden
                    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground"
                  >
                    {m.displayName.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{m.displayName}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.isLinkedUser ? `${m.email} · ${m.role}` : m.role}
                    </div>
                  </div>
                </div>
                {m.role !== 'owner' && (
                  <Button
                    type="button"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Ta bort medlem',
                        message: `Är du säker på att du vill ta bort "${m.displayName}" från hushållet?`,
                        confirmLabel: 'Ta bort',
                        destructive: true,
                      });
                      if (ok)
                        removeMutation.mutate({
                          memberId: m.id,
                        });
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-negative-soft hover:text-destructive"
                  >
                    Ta bort
                  </Button>
                )}
              </li>
            ),
          )}
        </ul>
      </Panel>

      {/* Add member (no account needed) */}
      <Panel
        title="Lägg till medlem"
        description="Lägg till en person i hushållet utan att de behöver ett konto. Användbart för att koppla utgifter och inkomster till rätt person."
      >
        <form onSubmit={handleAddMember} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label htmlFor="household-member-name">Medlemsnamn</Label>
            <Input
              id="household-member-name"
              value={newMemberName}
              onChange={(e) => setNewMemberName(e.target.value)}
              placeholder="t.ex. Kim"
              className="h-10"
            />
          </div>
          <Button
            type="submit"
            disabled={addMemberMutation.isPending}
            className="sm:self-end"
            size="lg"
          >
            {addMemberMutation.isPending ? 'Lägger till…' : 'Lägg till'}
          </Button>
        </form>
        {addMemberMutation.error && (
          <p className="mt-2 text-sm text-negative">{addMemberMutation.error.message}</p>
        )}
      </Panel>

      {/* Invite (requires account) */}
      <Panel
        title="Bjud in med konto"
        description="Bjud in en person som ska kunna logga in och hantera budgeten själv."
      >
        <form onSubmit={handleInvite} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label htmlFor="household-invite-email">E-postadress</Label>
            <Input
              id="household-invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="namn@exempel.se"
              className="h-10"
            />
          </div>
          <Button
            type="submit"
            disabled={inviteMutation.isPending}
            className="sm:self-end"
            size="lg"
          >
            {inviteMutation.isPending ? 'Bjuder in…' : 'Bjud in'}
          </Button>
        </form>
        {inviteMutation.error && (
          <p className="mt-2 text-sm text-negative">{inviteMutation.error.message}</p>
        )}
        {inviteLink && (
          <div className="mt-4 space-y-2 rounded-xl bg-muted/60 p-3">
            <Label htmlFor="household-invite-link" className="text-muted-foreground">
              Inbjudningslänk (kopiera och skicka):
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="household-invite-link"
                readOnly
                value={inviteLink}
                className="h-10 flex-1 bg-card font-mono text-xs text-muted-foreground"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                onClick={() => void navigator.clipboard.writeText(inviteLink)}
                variant="outline"
                size="lg"
              >
                Kopiera
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
